<div class="ai-page">
  <div class="ai-header">
    <div>
      <h1>AI-BOM</h1>
      <p>Re-quote an existing BOM against the live rate book, then catalog. This does not generate a BOM.</p>
    </div>
  </div>

  <div class="ai-tabs">
    <button type="button" class="tab-btn" [class.active]="view === 'quote'" (click)="setView('quote')">Quote</button>
    <button type="button" class="tab-btn" [class.active]="view === 'rates'" (click)="setView('rates')">Live rates</button>
  </div>

  <ng-container *ngIf="view === 'quote'">
    <div class="ai-card">
      <label class="field">
        <span class="field-label">Existing BOM</span>
        <select class="form-control" [(ngModel)]="selectedBomId" (ngModelChange)="onBomChange()" [disabled]="loadingList">
          <option [ngValue]="null">Select a BOM</option>
          <option *ngFor="let bom of boms" [ngValue]="bom.bom_id">
            {{ bom.bom_code }} — {{ bom.project_name || 'Project' }} ({{ bom.status }})
          </option>
        </select>
      </label>
      <button type="button" class="ghost-btn" (click)="loadQuote()" [disabled]="!selectedBomId || loadingQuote">
        {{ loadingQuote ? 'Quoting…' : 'Refresh rates' }}
      </button>
    </div>

    <div class="ai-empty" *ngIf="!quote && !loadingQuote">
      Select a BOM to see saved vs current rates.
    </div>

    <div class="ai-card stack" *ngIf="quote">
      <div class="summary">
        <div>
          <strong>{{ quote.bom.bom_code }}</strong>
          <span class="muted"> · {{ quote.bom.project_name }} · v{{ quote.bom.version }} · {{ quote.bom.status }}</span>
        </div>
        <div class="totals">
          <span>Saved {{ formatInr(quote.totals.saved_total) }}</span>
          <span>Current {{ formatInr(quote.totals.current_total) }}</span>
          <span [class.up]="quote.totals.delta > 0" [class.down]="quote.totals.delta < 0">
            Delta {{ formatInr(quote.totals.delta) }}
          </span>
        </div>
      </div>

      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th></th>
              <th>Material</th>
              <th>Qty</th>
              <th>BOM rate</th>
              <th>Current rate</th>
              <th>Delta</th>
              <th>Source</th>
              <th>Set live rate</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let line of quote.lines">
              <td>
                <input type="checkbox" [checked]="isSelected(line)" (change)="toggleLine(line, $any($event.target).checked)">
              </td>
              <td>
                <div>{{ line.material_code || '—' }} {{ line.material_name }}</div>
                <div class="muted" *ngIf="line.unit">{{ line.unit }}</div>
              </td>
              <td>{{ line.quantity }}</td>
              <td>{{ formatInr(line.saved_rate) }}</td>
              <td>{{ formatInr(line.current_rate) }}</td>
              <td [class.up]="line.delta > 0" [class.down]="line.delta < 0">
                {{ formatInr(line.delta) }}
                <span class="muted">{{ formatPct(line.delta_pct) }}</span>
              </td>
              <td>
                <span class="chip">{{ line.source }}</span>
                <div class="muted" *ngIf="line.as_of">as of {{ line.as_of | date:'mediumDate' }}</div>
              </td>
              <td>
                <div class="inline-rate" *ngIf="canSetLiveRate(line)">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    class="form-control rate-input"
                    [(ngModel)]="draftRates[line.material_id]"
                  >
                  <button
                    type="button"
                    class="ghost-btn"
                    (click)="saveInlineRate(line)"
                    [disabled]="savingMaterialId === line.material_id"
                  >
                    {{ savingMaterialId === line.material_id ? 'Saving…' : 'Save' }}
                  </button>
                </div>
                <span class="muted" *ngIf="!canSetLiveRate(line)">Live book</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="actions">
        <button type="button" class="primary-btn" (click)="applyRates()" [disabled]="applying || !selectedCount() || !canApplyInPlace()">
          Apply to this BOM
        </button>
        <button type="button" class="ghost-btn" (click)="saveAsVersion()" [disabled]="applying || !selectedCount()">
          Save as new version
        </button>
        <span class="muted" *ngIf="!canApplyInPlace()">In-place apply is only for draft or pending BOMs.</span>
      </div>
    </div>
  </ng-container>

  <ng-container *ngIf="view === 'rates'">
    <div class="ai-card">
      <label class="field">
        <span class="field-label">Material</span>
        <select class="form-control" [(ngModel)]="newMaterialId">
          <option [ngValue]="null">Select a material</option>
          <option *ngFor="let material of materials" [ngValue]="material.id">
            {{ materialLabel(material) }}
          </option>
        </select>
      </label>
      <label class="field field-narrow">
        <span class="field-label">Unit rate</span>
        <input type="number" min="0" step="0.01" class="form-control" [(ngModel)]="newUnitRate" placeholder="0.00">
      </label>
      <label class="field field-narrow">
        <span class="field-label">Region (optional)</span>
        <input type="text" class="form-control" [(ngModel)]="newRegion" placeholder="e.g. North">
      </label>
      <button type="button" class="primary-btn" (click)="addLiveRate()" [disabled]="savingRate">
        {{ savingRate ? 'Saving…' : 'Add live rate' }}
      </button>
    </div>

    <div class="ai-empty" *ngIf="!rates.length && !loadingRates">
      No live rates yet. Add one here, or save a rate from a catalog line on the Quote tab.
    </div>

    <div class="ai-card stack" *ngIf="rates.length">
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Material</th>
              <th>Live rate</th>
              <th>Catalog</th>
              <th>Source</th>
              <th>Region</th>
              <th>As of</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let row of rates">
              <td>
                <div>{{ row.material_code || '—' }} {{ row.material_name }}</div>
                <div class="muted" *ngIf="row.unit">{{ row.unit }}</div>
                <div class="muted" *ngIf="!row.owned">Inherited global</div>
              </td>
              <td>{{ formatInr(row.unit_rate) }}</td>
              <td>{{ row.catalog_rate != null ? formatInr(row.catalog_rate) : '—' }}</td>
              <td><span class="chip">{{ row.source }}</span></td>
              <td>{{ row.region || '—' }}</td>
              <td>{{ row.as_of ? (row.as_of | date:'mediumDate') : '—' }}</td>
              <td>
                <button type="button" class="ghost-btn" *ngIf="row.owned" (click)="removeLiveRate(row)">
                  Delete
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </ng-container>
</div>
